import { useMemo, useState } from "react";

import { type Message } from "shared/apiClient";
import { getText, interpolateString } from "shared/lib";
import { type QuestionData } from "@/components/wizard/lib";

import { createMessageEvent, useChannel } from "shared/channel";
import {
  CaptureEventType,
  useAccount,
  useCaptureEvent,
  useNoticeboardData,
  useTreeModuleExpertResult,
} from "@/hooks";

import { TreeActivity, TreeActivityData } from "./TreeActivity";
import {
  DescribeTreesContainer,
  DescribeTreesData,
} from "./DescribeTreesContainer";
import {
  CircumferenceTreePropertyDescriptor,
  HeightTreePropertyDescriptor,
  treeName,
  TreePropertyType,
} from "@/components/treeModule/lib";

export type TreeModuleStep = "tree_activity" | "describe_trees";

export interface TreeModuleStepProps {
  disabled?: boolean;
  message: Message;
  showLoading?: boolean;
}

export function TreeModuleStep({
  disabled,
  message,
  showLoading,
}: TreeModuleStepProps) {
  const NO_ACTIVITIES_SELECTED_RESPONSE_MESSAGE = useMemo(() => {
    return getText(
      "I will not be removing or pruning any trees, and will not be doing any works within 15m of any trees.",
    );
  }, []);

  const REMOVING_ACTIVITY_NOT_SELECTED_RESPONSE_MESSAGE = useMemo(() => {
    return getText("I will not be removing any trees.");
  }, []);
  const REMOVING_VEGETATION_SELECTED_RESPONSE_MESSAGE = useMemo(() => {
    return getText("Some vegetation will be removed.");
  }, []);
  const PRUNING_ACTIVITY_NOT_SELECTED_RESPONSE_MESSAGE = useMemo(() => {
    return getText("I will not be pruning any trees.");
  }, []);
  const DIGGING_ACTIVITY_NOT_SELECTED_RESPONSE_MESSAGE = useMemo(() => {
    return getText("I will not be doing works within 15m of any trees.");
  }, []);
  const REMOVING_BUT_NO_TREES_SELECTED_RESPONSE_MESSAGE = useMemo(() => {
    return getText(
      "I will be removing trees, but have not provided any further details.",
    );
  }, []);
  const PRUNING_BUT_NO_TREES_SELECTED_RESPONSE_MESSAGE = useMemo(() => {
    return getText(
      "I will be pruning trees, but have not provided any further details.",
    );
  }, []);
  const DIGGING_BUT_NO_TREES_SELECTED_RESPONSE_MESSAGE = useMemo(() => {
    return getText(
      "I will be doing works within 15m of trees, but have not provided any further details.",
    );
  }, []);

  const { captureEvent } = useCaptureEvent();

  const questionData = (message.question_data || {}) as QuestionData;

  const { channel } = useChannel();

  const { conversation } = useAccount();
  const { noticeboardData } = useNoticeboardData();
  const treeModuleExpertResult = useTreeModuleExpertResult(
    conversation,
    noticeboardData,
  );

  const [submitting, setSubmitting] = useState(false);
  const [treeModuleStep, setTreeModuleStep] =
    useState<TreeModuleStep>("tree_activity");
  const [treeActivityData, setTreeActivityData] = useState<TreeActivityData>(
    {},
  );

  const handleClickNext = (treeData: DescribeTreesData) => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      const text: Array<string> = [];
      if (treeData.removingTreeDescriptors.length > 0) {
        treeData.removingTreeDescriptors.forEach((treeDescriptor) => {
          const propertyMap = treeDescriptor.properties;
          text.push(
            interpolateString(
              "I will be removing {{treeTitle}}, which has the following properties:",
              {
                treeTitle: treeName(treeDescriptor),
              },
            ),
          );
          text.push(
            interpolateString("- Trunk type: {{trunkType}}", {
              trunkType: propertyMap.get(TreePropertyType.trunk_type)?.value,
            }),
          );
          text.push(
            interpolateString("- Species: {{species}}", {
              species: propertyMap.get(TreePropertyType.species)?.value,
            }),
          );
          if (propertyMap.has(TreePropertyType.dbh)) {
            text.push(
              interpolateString("- Diameter breast height: {{dbh}} m", {
                dbh: propertyMap.get(TreePropertyType.dbh)?.value,
              }),
            );
          }
          if (propertyMap.has(TreePropertyType.circumference)) {
            const circumference = propertyMap.get(
              TreePropertyType.circumference,
            ) as CircumferenceTreePropertyDescriptor;

            text.push(
              interpolateString(
                "- Circumference at {{distanceAboveGround}} above ground level: {{circumference}} m",
                {
                  distanceAboveGround: circumference.distanceAboveGround,
                  circumference: circumference.value,
                },
              ),
            );
            text.push(
              interpolateString(
                "- Circumference at ground level: {{groundLevelCircumference}} m",
                {
                  groundLevelCircumference:
                    circumference.groundLevelCircumference,
                },
              ) as string,
            );
          }
          if (propertyMap.has(TreePropertyType.height)) {
            const height = propertyMap.get(
              TreePropertyType.height,
            ) as HeightTreePropertyDescriptor;

            text.push(
              interpolateString("- Height: {{treeHeight}} m", {
                treeHeight: height.value,
              }),
            );
          }

          text.push(
            interpolateString("- Tree location: {{location}}", {
              location: propertyMap.get(TreePropertyType.location)?.value,
            }),
          );
          if (
            propertyMap.get(TreePropertyType.location)?.value === "off site"
          ) {
            text.push(
              interpolateString(
                `- Tree location description: {{locationDescription}}`,
                {
                  locationDescription: propertyMap.get(
                    TreePropertyType.location,
                  )?.["description"],
                },
              ),
            );
          }
          text.push(
            interpolateString("- Reason: {{reason}}", {
              reason: propertyMap.get(TreePropertyType.reason)?.value,
            }),
          );
          text.push("");
        });
      } else if (
        treeActivityData.isRemovingTrees &&
        ["trees", "both"].includes(treeActivityData.removingType || "")
      ) {
        text.push(REMOVING_BUT_NO_TREES_SELECTED_RESPONSE_MESSAGE);
        text.push("");
      } else {
        text.push(REMOVING_ACTIVITY_NOT_SELECTED_RESPONSE_MESSAGE);
        text.push("");
      }

      if (
        treeActivityData.isRemovingTrees &&
        ["vegetation", "both"].includes(treeActivityData.removingType || "")
      ) {
        text.push(REMOVING_VEGETATION_SELECTED_RESPONSE_MESSAGE);
        text.push("");
      }

      if (treeData.pruningTreeDescriptors.length > 0) {
        treeData.pruningTreeDescriptors.forEach((treeDescriptor) => {
          const propertyMap = treeDescriptor.properties;
          text.push(
            interpolateString(
              "I will be pruning {{treeTitle}}, which has the following properties:",
              {
                treeTitle: treeName(treeDescriptor),
              },
            ),
          );
          text.push(
            interpolateString("- Trunk type: {{trunkType}}", {
              trunkType: propertyMap.get(TreePropertyType.trunk_type)?.value,
            }),
          );
          text.push(
            interpolateString("- Species: {{species}}", {
              species: propertyMap.get(TreePropertyType.species)?.value,
            }),
          );
          if (propertyMap.has(TreePropertyType.dbh)) {
            text.push(
              interpolateString("- Diameter breast height: {{dbh}} m", {
                dbh: propertyMap.get(TreePropertyType.dbh)?.value,
              }),
            );
          }
          if (propertyMap.has(TreePropertyType.circumference)) {
            const circumference = propertyMap.get(
              TreePropertyType.circumference,
            ) as CircumferenceTreePropertyDescriptor;
            text.push(
              interpolateString(
                "- Circumference at {{distanceAboveGround}} above ground level: {{circumference}} m",
                {
                  distanceAboveGround: circumference.distanceAboveGround,
                  circumference: circumference.value,
                },
              ),
            );
            text.push(
              interpolateString(
                "- Circumference at ground level: {{groundLevelCircumference}} m",
                {
                  groundLevelCircumference:
                    circumference.groundLevelCircumference,
                },
              ),
            );
          }
          text.push(
            interpolateString(
              "- Lopping/pruning percentage: {{lopPrunePercentage}} %",
              {
                lopPrunePercentage:
                  parseFloat(
                    propertyMap.get(TreePropertyType.lop_prune_percentage)
                      ?.value || "0",
                  ) * 100,
              },
            ),
          );
          text.push(
            interpolateString("- Tree location: {{location}}", {
              location: propertyMap.get(TreePropertyType.location)?.value,
            }),
          );
          if (
            propertyMap.get(TreePropertyType.location)?.value === "off site"
          ) {
            text.push(
              interpolateString(
                `- Tree location description: {{locationDescription}}`,
                {
                  locationDescription: propertyMap.get(
                    TreePropertyType.location,
                  )?.["description"],
                },
              ),
            );
          }
          text.push(
            interpolateString("- Reason: {{reason}}", {
              reason: propertyMap.get(TreePropertyType.reason)?.value,
            }),
          );
          text.push("");
        });
      } else if (treeActivityData.isPruningTrees) {
        text.push(PRUNING_BUT_NO_TREES_SELECTED_RESPONSE_MESSAGE);
        text.push("");
      } else {
        text.push(PRUNING_ACTIVITY_NOT_SELECTED_RESPONSE_MESSAGE);
        text.push("");
      }

      if (treeData.diggingTreeDescriptors.length > 0) {
        treeData.diggingTreeDescriptors.forEach((treeDescriptor) => {
          const propertyMap = treeDescriptor.properties;
          text.push(
            interpolateString(
              "I will be digging near {{treeTitle}}, which has the following properties:",
              {
                treeTitle: treeName(treeDescriptor),
              },
            ),
          );
          text.push(
            interpolateString("- Trunk type: {{trunkType}}", {
              trunkType: propertyMap.get(TreePropertyType.trunk_type)?.value,
            }),
          );
          text.push(
            interpolateString("- Species: {{species}}", {
              species: propertyMap.get(TreePropertyType.species)?.value,
            }),
          );
          if (propertyMap.has(TreePropertyType.dbh)) {
            text.push(
              interpolateString("- Diameter breast height: {{dbh}} m", {
                dbh: propertyMap.get(TreePropertyType.dbh)?.value,
              }),
            );
          }
          if (propertyMap.has(TreePropertyType.circumference)) {
            const circumference = propertyMap.get(
              TreePropertyType.circumference,
            ) as CircumferenceTreePropertyDescriptor;
            text.push(
              interpolateString(
                "- Circumference at {{distanceAboveGround}} above ground level: {{circumference}} m",
                {
                  distanceAboveGround: circumference.distanceAboveGround,
                  circumference: circumference.value,
                },
              ),
            );
            text.push(
              interpolateString(
                "- Circumference at ground level: {{groundLevelCircumference}} m",
                {
                  groundLevelCircumference:
                    circumference.groundLevelCircumference,
                },
              ),
            );
          }
          if (propertyMap.has(TreePropertyType.height)) {
            const height = propertyMap.get(
              TreePropertyType.height,
            ) as HeightTreePropertyDescriptor;

            text.push(
              interpolateString("- Height: {{treeHeight}} m", {
                treeHeight: height.value,
              }),
            );
          }
          text.push(
            interpolateString("- Distance from tree: {{distance}} m", {
              distance: propertyMap.get(TreePropertyType.distance)?.value,
            }),
          );
          text.push(
            interpolateString("- Tree location: {{location}}", {
              location: propertyMap.get(TreePropertyType.location)?.value,
            }),
          );
          if (
            propertyMap.get(TreePropertyType.location)?.value === "off site"
          ) {
            text.push(
              interpolateString(
                `- Tree location description: {{locationDescription}}`,
                {
                  locationDescription: propertyMap.get(
                    TreePropertyType.location,
                  )?.["description"],
                },
              ),
            );
          }
          text.push(
            interpolateString("- Altering ground level: {{excavation}}", {
              excavation: propertyMap.get(TreePropertyType.excavation)?.value,
            }),
          );
          text.push("");
        });
      } else if (treeActivityData.isDiggingTrees) {
        text.push(DIGGING_BUT_NO_TREES_SELECTED_RESPONSE_MESSAGE);
        text.push("");
      } else {
        text.push(DIGGING_ACTIVITY_NOT_SELECTED_RESPONSE_MESSAGE);
        text.push("");
      }

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        text: text.join("\n"),
      });

      console.log("<TreeModuleStep> handleClickNext()", messageDescriptor);
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  const handleClickNextTreeActivities = () => {
    if (
      treeActivityData.isRemovingTrees ||
      treeActivityData.isPruningTrees ||
      treeActivityData.isDiggingTrees
    ) {
      setTreeModuleStep("describe_trees");
    } else {
      if (channel && conversation?.id) {
        setSubmitting(true);

        const messageDescriptor = createMessageEvent({
          conversation_id: conversation.id,
          text: NO_ACTIVITIES_SELECTED_RESPONSE_MESSAGE,
        });

        console.log("<TreeModuleStep> handleClickNext()", messageDescriptor);
        channel.sendMessage(messageDescriptor);

        captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
          messageId: message.id,
        });
      }
    }
  };

  if (treeModuleStep === "tree_activity") {
    return (
      <TreeActivity
        onChangeTreeActivityData={setTreeActivityData}
        onClickNext={handleClickNextTreeActivities}
        questionData={questionData}
        showLoading={showLoading}
        treeActivityData={treeActivityData}
      />
    );
  }

  if (treeModuleStep === "describe_trees") {
    return (
      <DescribeTreesContainer
        councilCode={conversation?.council_code || ""}
        removing={
          !!treeActivityData.isRemovingTrees &&
          ["trees", "both"].includes(treeActivityData.removingType || "")
        }
        disabled={disabled}
        digging={!!treeActivityData.isDiggingTrees}
        onClickNext={handleClickNext}
        pruning={!!treeActivityData.isPruningTrees}
        showLoading={showLoading}
        submitting={submitting}
        treeModuleExpertResult={treeModuleExpertResult}
      />
    );
  }
}
